import { useHistorySync } from './lib/useHistorySync.js';
/* OpenQwenChat — main app */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Ic } from "./components/Icons.jsx";
import { useDemoServices } from "./lib/DemoContext.jsx";
import Sidebar, { SystemPromptModal } from "./components/Sidebar.jsx";
import { ChatView } from "./components/Chat.jsx";
import LiveMode from "./components/Live.jsx";
import TextCourse from "./components/TextCourse.jsx";
import { NewTherapyDialog, DeleteChatDialog } from "./components/WorkspaceDialogs.jsx";
import {
  useTweaks, TweaksPanel, TweakSection, TweakColor, TweakToggle, TweakRadio, TweakSelect,
} from "./components/TweaksPanel.jsx";
import { MODEL_DISPLAY, visibleModels, defaultModel } from "./lib/modelPicker.js";
import { streamReply } from "./lib/chatClient.js";
import { createModelConversations, modelSessionKey } from "./lib/modelSessions.js";
import { readTherapyNavigation, writeTherapyNavigation, restoredTherapyModel } from './lib/therapyNavigation.js';

// Dictate records mic PCM16@16k through this worklet, then transcribes via the
// Omni model (/api/transcribe) instead of the browser's Web Speech API.
const recorderUrl = new URL("./worklets/pcm-recorder.js", import.meta.url);

function mergeInt16(chunks) {
  let n = 0;
  for (const c of chunks) n += c.length;
  const out = new Int16Array(n);
  let o = 0;
  for (const c of chunks) { out.set(c, o); o += c.length; }
  return out;
}

function pcmToWavBase64(int16, sampleRate) {
  const dataLen = int16.length * 2;
  const buf = new ArrayBuffer(44 + dataLen);
  const dv = new DataView(buf);
  const w = (off, s) => { for (let i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i)); };
  w(0, "RIFF"); dv.setUint32(4, 36 + dataLen, true); w(8, "WAVE");
  w(12, "fmt "); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, sampleRate, true); dv.setUint32(28, sampleRate * 2, true);
  dv.setUint16(32, 2, true); dv.setUint16(34, 16, true);
  w(36, "data"); dv.setUint32(40, dataLen, true);
  let off = 44;
  for (let i = 0; i < int16.length; i++) { dv.setInt16(off, int16[i], true); off += 2; }
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

// Default assistant system prompt. Mirrors the gateway's DEFAULT_INSTRUCTIONS
// (config.py / .env.example): TTS-friendly, plain spoken language. Used as the
// "恢复默认" target for the avatar-menu System Prompt editor; an empty value
// makes the gateway apply its own DEFAULT_INSTRUCTIONS fallback.
const DEFAULT_SYSTEM_PROMPT =
  "You are Qwen3-Omni, a friendly multimodal assistant. Reply concisely in the user's language. " +
  "Always answer in natural, conversational spoken language: plain sentences a person would actually say out loud. " +
  "Do not use markdown, headings, bullet or numbered lists, tables, code blocks, emoji, or special symbols, since your reply may be read aloud by text-to-speech.";

const TWEAK_DEFAULTS = {
  accent: "#5b5bf0",
  dark: false,
  voiceViz: "orb",
  transport: "ws",
  voice: "Chelsie",
  // Assistant system prompt, editable from the avatar menu. Flows to text chat
  // (chatClient) and realtime voice/video (Live -> RealtimeClient instructions).
  // "" = use the model's own default (the gateway resolves client -> model ->
  // global). The counselling finetune ships the prompt it was TRAINED with, so
  // hardcoding a generic one here silently served it off-distribution.
  systemPrompt: "",
  // Upstream conversation mode for the local Qwen3-Omni: "chat" = interleaved
  // /v1/chat/completions, "realtime" = native /v1/realtime WS (separate
  // upstream). Switching reopens the Live session. Ignored by cloud models.
  realtimeMode: "chat",
  // Let the model emit its <think> reasoning: it shows in the text pane, while the
  // gateway's two-pass turn keeps it out of the spoken audio. ON by default
  // because LynciaCounselor is TRAINED to think — its system prompt makes the
  // think block part of the format contract. Only meaningful in "chat" mode; the
  // native /v1/realtime upstream templates its own prompt.
  thinking: true,
  // Digital human (FlashTalk talking portrait). Default off, mirroring the
  // gateway's DIGITAL_HUMAN_ENABLED flag; when on, Live mode renders the
  // assistant's portrait (JPEG frames over the WS transport) instead of the orb.
  digitalHuman: false,
  portrait: "",
};

// Client-side extras (desc/badge/ordering) keyed by gateway registry id
// (config.model_registry()). Names and capabilities come from GET /api/models —
// this list only decorates and orders; `name` is the pre-fetch fallback label.
// `video:false` models are realtime voice agents bridged server-side (no camera).
const MODELS = [
  { id: "qwen3-omni", name: "Qwen3-Omni", desc: "基座模型 · Text · voice · vision · interleaved realtime", badge: "Omni", video: true },
  { id: "qwen3-omni-ft", name: "LynciaCounselor-Omni", desc: "GSPO 微调 thinker（共用基座 talker）· 对照基座模型", badge: "SFT", video: true },
  { id: "counselor-e1-think", name: "E1-Counselor（think）", desc: "E1 音频 SFT · 带思维链 · 个案概念化滚动", badge: "E1", video: false },
  { id: "counselor-e1-tts", name: "E1-Counselor（think+基座TTS）", desc: "E1 音频 SFT · 思考与概念化不进 talker · 基座朗读", badge: "E1", video: false },
  { id: "counselor-e1-plain", name: "E1-Counselor", desc: "E1 音频 SFT · 无思维链 · 个案概念化滚动", badge: "E1", video: false },
  { id: "minicpmo", name: "MiniCPM-o 4.5", desc: "独立服务 · 先出文本再出语音（非交错）· 参考音色", badge: "MiniCPM", video: true },
  { id: "openai-realtime", name: "OpenAI Realtime", desc: "实时语音对话（仅语音）", badge: "Realtime", video: false },
  { id: "gemini-live", name: "Gemini Live", desc: "实时语音对话（仅语音）", badge: "Live", video: false },
  { id: "grok-voice", name: "Grok Voice", desc: "实时语音对话（仅语音）", badge: "Voice", video: false },
];

// Pre-fetch fallback for the Thinking switch; once /api/models answers, its
// per-model `supports_thinking` flag wins, so registering the next reasoning
// finetune in the gateway is enough — no frontend edit needed.
const THINKING_MODELS = new Set(["qwen3-omni-ft", "counselor-e1-think"]);

// Product display names do not change the registry ID or pinned release.
function ModelName({ id, fallback }) {
  const display = MODEL_DISPLAY[id];
  return <><span className="model-name">{display?.name || fallback}</span>{display && <small className="model-version">{display.version}</small>}</>;
}

const SEED_HISTORY = [
  { id: "h1", title: "你好，我是你的助手", seed: [
    { role: "user", text: "你好，你是谁？" },
    { role: "assistant", text: "你好！我是 **Qwen3-Omni**，你的多模态助手。我可以用文字聊天，也能实时语音对话，甚至通过摄像头“看”东西。今天想聊点什么？" },
  ]},
  { id: "h2", title: "闲聊时光", seed: [
    { role: "user", text: "随便聊聊吧" },
    { role: "assistant", text: "好呀～ 那我先开个头：你最近有没有看到什么有意思的东西？\n\n点右下角的波形可以直接语音聊天，更自然一些。" },
  ]},
  { id: "h3", title: "你好，有什么可以帮助你?", seed: [] },
];

let MID = 1;
const mkMsg = (role, text, streaming = false) => ({ id: MID++, role, text, streaming });

/** @param {{accountId?: string, accountFooter?: import("react").ReactNode, accountTheme?: string | null, accountNotice?: import("react").ReactNode, thinkingDescription?: string, locale?: string}} props */
export default function App({ accountId = '', accountFooter = null, accountTheme = null, accountNotice = null,
  locale = "en",
  thinkingDescription = "Thinking：开启后在文字面板显示思考过程；语音只朗读回复正文" }) {
  const services = useDemoServices();
  const [promptOpen, setPromptOpen] = useState(false);
  const [realHistory, setRealHistory] = useState([]);
  const historyRevision = useRef(0);
  const selectionRevision = useRef(0);
  const refreshHistory = () => {
    const revision = ++historyRevision.current;
    return services?.history().then(rows => { if (revision === historyRevision.current) setRealHistory(rows); }).catch(() => {});
  };
  useEffect(() => { refreshHistory(); }, [services]);
  useHistorySync(refreshHistory);
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS, { persist: !services });
  const [collapsed, setCollapsed] = useState(false);
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 1023px)').matches);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const workspaceRef = useRef(null);
  useEffect(() => {
    const viewport = window.visualViewport;
    const node = workspaceRef.current;
    if (!narrow || !viewport || !node) return;
    // Mobile keyboards can shrink the visual viewport without changing 100dvh.
    // Leave pinch zoom to the browser instead of resizing the application for it.
    const resize = () => {
      if (viewport.scale === 1) node.style.setProperty('--workspace-height', `${viewport.height}px`);
      else node.style.removeProperty('--workspace-height');
    };
    resize();
    viewport.addEventListener('resize', resize);
    return () => { viewport.removeEventListener('resize', resize); node.style.removeProperty('--workspace-height'); };
  }, [narrow]);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 1023px)');
    const resize = () => { setNarrow(query.matches); setDrawerOpen(false); };
    query.addEventListener('change', resize);
    return () => query.removeEventListener('change', resize);
  }, []);
  const [restoredNavigation] = useState(() => readTherapyNavigation(history.state, location.hash, accountId));
  const restorationError = locale.startsWith('zh') ? '无法恢复原咨询，请从侧栏重新选择。' : 'Could not restore the consultation. Select it again in the sidebar.';
  const initialMode = (() => {
    if (restoredNavigation?.status === 'invalid') return 'text';
    const h = location.hash.replace(/^#/, "");
    return (h === "voice" || h === "video") ? h : "text";
  })();
  const [mode, setMode] = useState(initialMode); // text | voice | video
  const [textCourse, setTextCourse] = useState(false);
  const [therapyCreating, setTherapyCreating] = useState(false);
  const [therapyCreateError, setTherapyCreateError] = useState(restoredNavigation?.status === 'invalid' ? restorationError : '');
  const pendingTherapy = useRef(null);
  const [newTherapyOpen, setNewTherapyOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [therapyStartCourse, setTherapyStartCourse] = useState('');
  const [voiceStartCourse, setVoiceStartCourse] = useState(restoredNavigation?.status === 'valid' ? restoredNavigation.courseId : '');
  const [therapyProgress, setTherapyProgress] = useState(null);
  const [therapyRevision, setTherapyRevision] = useState(0);
  const [therapyEpoch, setTherapyEpoch] = useState(0);
  const tr = (zh, en) => locale.startsWith('zh') ? zh : en;
  const onTherapyProgress = useCallback(progress => setTherapyProgress(progress), []);
  const refreshTherapy = useCallback(() => setTherapyRevision(value => value + 1), []);
  const archivePending = !therapyProgress?.archiveQueued && ['archiving', 'archive_retry', 'archive_disconnected'].includes(therapyProgress?.status);
  const leaveTherapy = () => {
    if (archivePending) return false;
    if (textCourse && !therapyProgress?.archiveQueued && therapyProgress?.courseId && !['finished', 'select', 'interrupted'].includes(therapyProgress.status) &&
        !window.confirm(tr('当前咨询尚未归档，离开会断开连接。确认离开？', 'This session has not been archived. Leaving will disconnect it. Leave?'))) return false;
    setTextCourse(false); setTherapyProgress(null); return true;
  };
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [model, setModel] = useState({ id: "", name: "", video: false });
  const conversations = useRef(createModelConversations(""));
  const conversationIds = useRef(new Map());
  const requestRef = useRef(null);
  const activeTurn = useRef(null);
  const [turnId, setTurnId] = useState(null);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState('');
  const modelChosen = useRef(false);
  const chooseModel = (next, courseId = '') => {
    if (next.id === model.id) return true;
    if (pendingTherapy.current) { setNewTherapyOpen(true); return false; }
    if (therapyCreating || !leaveTherapy()) return false;
    selectionRevision.current += 1; setHistoryLoading(false);
    modelChosen.current = true;
    setVoiceStartCourse(courseId);
    setTherapyCreateError('');
    setStopError(''); setStopping(false); activeTurn.current = null;
    requestRef.current?.abort();
    setMessages(conversations.current.select(next.id));
    setDraft("");
    setActiveId(conversationIds.current.get(next.id) || null);
    setModel(next);
    return true;
  };
  // Per-model registry facts as reported by the gateway (/api/models): label,
  // default system prompt (e.g. LynciaCounselor's training prompt), and whether
  // the model reasons — so the UI reflects what is actually registered instead
  // of a client-side guess.
  const [modelDefaults, setModelDefaults] = useState({});
  // Registry models the client-side MODELS list doesn't know (e.g. the
  // env-gated r10 slot qwen3-omni-ft2): appended to the picker generically,
  // so a new finetune needs a config.py entry and nothing else.
  const [extraModels, setExtraModels] = useState([]);
  // Registry ids as reported by the gateway; null until /api/models answers.
  // The picker is FILTERED by this set, so models the gateway drops (e.g. the
  // E1 trio under E1_ENABLED=false) disappear instead of lingering as dead
  // static entries. Before the fetch resolves, show a neutral loading state.
  const [registryIds, setRegistryIds] = useState(null);
  const [modelError, setModelError] = useState(false);
  const [modelAttempt, setModelAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    setModelError(false);
    (services ? services.models() : fetch("/api/models").then(r => r.json()))
      .then((d) => {
        if (!alive) return;
        const models = d.models || [];
        if (!visibleModels(models).length) throw new Error("No available models");
        setModelDefaults(Object.fromEntries(
          models.map((m) => [m.id, {
            label: m.label || "",
            instructions: m.instructions || "",
            supportsThinking: !!m.supports_thinking,
            coursePromptVersion: m.course_prompt_version || "",
            courseCapable: !!m.course_capable,
            harnessProfile: m.harness_profile || "",
            releaseId: m.release_id || "",
          }])));
        const known = new Set(MODELS.map((m) => m.id));
        setExtraModels(models.filter((m) => !known.has(m.id)).map((m) => ({
          id: m.id, name: m.label || m.id,
          desc: "网关注册模型", badge: "New",
          video: m.video !== false && !m.voice_only,
        })));
        setRegistryIds(new Set(models.map((m) => m.id)));
        // Prefer the registered v1.0 display option on first load, without
        // replacing a user choice or an existing conversation's model binding.
        if (!modelChosen.current) {
          const restoredModel = restoredTherapyModel(restoredNavigation, visibleModels(models));
          const selected = restoredModel || defaultModel(models, d.default);
          if (selected) chooseModel({
            ...(MODELS.find((m) => m.id === selected.id) || { desc: "网关注册模型", badge: "Omni" }),
            id: selected.id, name: selected.label || selected.id,
            video: selected.video !== false && !selected.voice_only,
          }, restoredModel ? restoredNavigation.courseId : '');
          if (restoredNavigation && !restoredModel) {
            setMode('text'); setVoiceStartCourse(''); setTherapyCreateError(restorationError);
          }
        }
      })
      .catch(() => { if (alive) setModelError(true); });
    return () => { alive = false; };
  }, [services, modelAttempt]);
  const pickerModels = visibleModels([
    ...(registryIds ? MODELS.filter((m) => registryIds.has(m.id)) : []),
    ...extraModels,
  ]);
  // If the selected model vanished from the registry (e.g. an E1 entry while
  // E1 is offline), fall back to the first registered one.
  useEffect(() => {
    if (registryIds && !registryIds.has(model.id)) {
      const first = pickerModels[0];
      if (first) chooseModel(first);
    }
  }, [registryIds]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dictating, setDictating] = useState(false);
  const recRef = useRef(null);
  useEffect(() => () => { requestRef.current?.abort(); }, []);

  // apply theme + accent
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", accountTheme || (t.dark ? "dark" : "light"));
    if (services) return;
    const v = t.accent || "#5b5bf0";
    const root = document.documentElement.style;
    root.setProperty("--accent", v);
    root.setProperty("--accent-strong", `color-mix(in oklab, ${v} 86%, black)`);
    root.setProperty("--accent-weak", `color-mix(in oklab, ${v} 13%, transparent)`);
  }, [t.dark, t.accent, accountTheme, services]);

  useEffect(() => {
    if (!registryIds) return;
    const target = mode === "text" ? "" : "#" + mode;
    const navigation = writeTherapyNavigation(history.state, { accountId, modelId: model.id, courseId: voiceStartCourse, mode });
    history.replaceState(navigation, "", location.pathname + location.search + target);
  }, [mode, voiceStartCourse, model.id, registryIds, accountId]);

  const replaceConversation = (next) => {
    selectionRevision.current += 1; setHistoryLoading(false); setHistoryError('');
    setStopError(''); setStopping(false); activeTurn.current = null;
    requestRef.current?.abort();
    setMessages(conversations.current.replace(next));
  };
  const startTherapy = (courseId, requestedMode = 'voice') => {
    if (!['voice', 'video'].includes(requestedMode)) return;
    if (requestedMode === 'video' && (model.video === false || !courseId)) return;
    selectionRevision.current += 1; setHistoryLoading(false);
    if (!modelDefaults[model.id]?.courseCapable || archivePending || therapyCreating) return;
    if (textCourse && !['finished', 'select', 'error', 'closed'].includes(therapyProgress?.status) && therapyProgress?.courseId === courseId) return;
    if (!leaveTherapy()) return;
    requestRef.current?.abort();
    setTherapyEpoch(n => n + 1);
    if (!courseId && !services) {
      setTherapyStartCourse(''); setTextCourse(true); setMode('text');
      return;
    }
    setVoiceStartCourse(courseId); setTextCourse(false); setMode(requestedMode);
  };
  const newTherapy = async () => {
    if (!modelDefaults[model.id]?.courseCapable || archivePending || therapyCreating || !leaveTherapy()) return;
    if (!services) { startTherapy(''); return; }
    setTherapyCreating(true); setTherapyCreateError('');
    selectionRevision.current += 1; setHistoryLoading(false);
    // Keep the same creation key for explicit retries after an unknown response.
    if (!pendingTherapy.current) pendingTherapy.current = {model:model.id, request_id:crypto.randomUUID()};
    requestRef.current?.abort();
    try {
      const result = await services.courseClient(model.id).create({request_id:pendingTherapy.current.request_id});
      setVoiceStartCourse(result.course_id);
      pendingTherapy.current = null; setNewTherapyOpen(false);
      setTextCourse(false); setMode('voice'); refreshTherapy();
    } catch { setTherapyCreateError(tr('创建结果尚未确认。请重试同一次创建。', 'Creation is unconfirmed. Retry the same request.')); }
    finally { setTherapyCreating(false); }
  };
  const returnHome = () => {
    if (archivePending) return;
    setMode('text'); setTextCourse(false); setTherapyProgress(null);
    setTherapyStartCourse(''); setVoiceStartCourse('');
    services?.reset(model.id); conversationIds.current.delete(model.id);
    replaceConversation([]); setDraft(''); setActiveId(null); refreshTherapy();
  };
  const newChat = () => { if (leaveTherapy()) returnHome(); };
  const startMediaChat = (nextMode) => {
    if (!leaveTherapy()) return;
    requestRef.current?.abort();
    setVoiceStartCourse(''); setTherapyStartCourse('');
    setMode(nextMode);
  };
  const selectHistory = async (id) => {
    if (!leaveTherapy()) return;
    setMode("text");
    if (services) {
      const row = realHistory.find(item => item.id === id);
      if (!row) return;
      requestRef.current?.abort();
      const revision = ++selectionRevision.current;
      setHistoryLoading(true); setHistoryError('');
      try {
        const records = await services.messages(id);
        if (revision !== selectionRevision.current) return;
        if (!chooseModel({ ...(pickerModels.find(m => m.id === row.model_id) || { id: row.model_id, name: row.model_id }), id: row.model_id })) return;
        services.select(row.model_id, id);
        conversationIds.current.set(row.model_id, id);
        setActiveId(id);
        replaceConversation(records.messages.map(m => mkMsg(m.role, m.display_text)));
        setDraft("");
      } catch {
        if (revision === selectionRevision.current) setHistoryError(tr('无法读取聊天记录，请重试。', 'Could not load this chat. Please retry.'));
      } finally { if (revision === selectionRevision.current) setHistoryLoading(false); }
      return;
    }
    const h = SEED_HISTORY.find((x) => x.id === id);
    setActiveId(id);
    replaceConversation((h?.seed || []).map((m) => mkMsg(m.role, m.text)));
    setDraft("");
  };

  const send = async (textArg) => {
    const text = (textArg ?? draft).trim();
    if (!text || historyLoading || deleting || (requestRef.current && !requestRef.current.signal.aborted)) return;
    setTurnId(null); setStopping(false); setStopError('');
    setDraft("");
    const hist = messages.filter((m) => m.policyEligible !== false).map((m) => ({ role: m.role, text: m.text }));
    const userMsg = mkMsg("user", text);
    const aMsg = { ...mkMsg("assistant", "", true), thinkingExpected: !!modelDefaults[model.id]?.supportsThinking && t.thinking };
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    conversations.current.replace([...messages, userMsg]);
    const turn = conversations.current.beginReply(aMsg);
    activeTurn.current = { token: turn, id: null };
    setMessages(conversations.current.read());
    const update = (text, done, result) => {
      const next = conversations.current.updateReply(turn, text, done, result);
      if (next) setMessages(next);
    };
    await (services ? services.streamReply : streamReply)({
      prompt: text,
      history: hist,
      model: model.id,
      systemPrompt: t.systemPrompt,
      thinking: t.thinking,
      signal: controller.signal,
      shouldStop: () => !conversations.current.isCurrent(turn),
      onSession: id => { if (conversations.current.isCurrent(turn)) { conversationIds.current.set(model.id, id); setActiveId(id); } },
      onCreated: (id) => {
        if (!conversations.current.isCurrent(turn)) return;
        activeTurn.current = { token: turn, id }; setTurnId(id); refreshHistory();
      },
      onToken: (acc) => update(acc, false),
      onDone: (acc, result) => {
        update(acc, true, result);
        if (activeTurn.current?.token === turn) { activeTurn.current = null; setTurnId(null); setStopError(''); }
        refreshHistory();
      },
    });
    if (requestRef.current === controller) { requestRef.current = null; setStopping(false); }
  };

  const deleteChat = async () => {
    if (!deleteTarget || deleting || messages.some(message => message.streaming)) return;
    setDeleting(true); setDeleteError('');
    const target = deleteTarget;
    try {
      await services.deleteConversation(target.id);
      historyRevision.current += 1;
      setRealHistory(rows => rows.filter(row => row.id !== target.id));
      if (conversationIds.current.get(target.model_id) === target.id) {
        conversationIds.current.delete(target.model_id);
        conversations.current.remove(target.model_id);
        services.reset(target.model_id);
      }
      if (activeId === target.id) { replaceConversation([]); setDraft(''); setActiveId(null); }
      setDeleteTarget(null);
    } catch { setDeleteError(tr('删除未完成，记录仍保留，请重试。', 'Could not delete this chat. It is still available; please retry.')); }
    finally { setDeleting(false); }
  };

  const stopReply = async () => {
    const current = activeTurn.current;
    if (!current || !conversations.current.isCurrent(current.token) || stopping) return;
    if (!services) { requestRef.current?.abort(); return; }
    if (!current.id) return;
    setStopping(true); setStopError('');
    try { await services.cancelTurn(current.id); }
    catch {
      if (activeTurn.current === current && conversations.current.isCurrent(current.token)) {
        setStopping(false);
        setStopError(tr('停止请求未确认，请重试。', 'The stop request was not confirmed. Please retry.'));
      }
    }
  };

  // dictation via the Omni model: record mic PCM16@16k, then POST the WAV to
  // /api/transcribe (no browser Web Speech API, no Google round-trip).
  const toggleDictate = async () => {
    if (dictating) {
      // stop recording -> transcribe with the model
      const r = recRef.current; recRef.current = null; setDictating(false);
      if (!r) return;
      try { r.node.disconnect(); r.src.disconnect(); } catch {}
      try { r.stream.getTracks().forEach((t) => t.stop()); } catch {}
      try { await r.ctx.close(); } catch {}
      const pcm = mergeInt16(r.chunks);
      if (pcm.length < 1600) return; // <100 ms captured — ignore
      const base = draft ? draft.trimEnd() + " " : "";
      setDraft(base + "…");
      try {
        const body = { audio: pcmToWavBase64(pcm, 16000), model: model.id };
        const resp = services ? null : await fetch("/api/transcribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const j = services ? await services.transcribe(body) : await resp.json();
        setDraft((base + (j.text || "")).trim());
      } catch (e) {
        console.warn("[dictate]", e);
        setDraft(base.trim());
      }
      return;
    }
    // start recording
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setDraft((d) => d || "麦克风不可用，请检查权限或改用键盘输入。");
      return;
    }
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC({ sampleRate: 16000 });
      await ctx.audioWorklet.addModule(recorderUrl);
      const src = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, "pcm-recorder");
      const chunks = [];
      node.port.onmessage = (e) => chunks.push(new Int16Array(e.data));
      const zero = ctx.createGain(); zero.gain.value = 0; // keep the graph pulling
      src.connect(node); node.connect(zero); zero.connect(ctx.destination);
      recRef.current = { ctx, stream, src, node, chunks };
      setDictating(true);
    } catch (e) {
      console.warn("[dictate]", e);
      stream.getTracks().forEach((t) => t.stop());
      setDraft((d) => d || "无法初始化录音。");
    }
  };

  // close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const h = () => setMenuOpen(false);
    window.addEventListener("click", h);
    return () => window.removeEventListener("click", h);
  }, [menuOpen]);

  const screenLabel = mode === "text"
    ? (textCourse ? "Text · Course" : (messages.length ? "Text · Conversation" : "Text · Home"))
    : mode === "voice" ? "Voice mode" : "Video mode";

  return (
    <div ref={workspaceRef} className="app" data-screen-label={screenLabel} lang={locale}>
      <Sidebar accountFooter={accountFooter} locale={locale}
        narrow={narrow} drawerOpen={drawerOpen} onCloseDrawer={closeDrawer}
        onNewTherapy={() => { closeDrawer(); setNewTherapyOpen(true); }} courseCapable={!!modelDefaults[model.id]?.courseCapable}
        therapyRevision={therapyRevision} therapyProgress={therapyProgress}
        onTherapyDeleted={id => { if (therapyProgress?.courseId === id) { setTextCourse(false); setTherapyProgress(null); setTherapyStartCourse(''); } }}
        onContinueTherapy={(id, mode) => { closeDrawer(); startTherapy(id, mode); }} canVideoTherapy={model.video !== false}
        navigationDisabled={archivePending || therapyCreating || deleting || !registryIds}
        therapyKey={modelSessionKey(model.id, modelDefaults[model.id])}
        collapsed={collapsed}
        onToggle={() => narrow ? closeDrawer() : setCollapsed((c) => !c)}
        onNewChat={() => { closeDrawer(); newChat(); }}
        onDeleteChat={services ? row => { setDeleteTarget(row); setDeleteError(''); } : undefined}
        deleteDisabled={historyLoading || messages.some(message => message.streaming)}
        activeId={activeId}
        history={services ? realHistory : SEED_HISTORY}
        onSelect={id => { closeDrawer(); void selectHistory(id); }}
        systemPrompt={t.systemPrompt}
        defaultSystemPrompt={modelDefaults[model.id]?.instructions || DEFAULT_SYSTEM_PROMPT}
        onSystemPromptChange={(v) => setTweak("systemPrompt", v)}
        modelId={model.id}
        realtimeMode={t.realtimeMode}
        onRealtimeModeChange={(v) => setTweak("realtimeMode", v)}
      />

      {narrow && drawerOpen && <div className="sidebar-scrim" aria-hidden="true" onClick={closeDrawer} />}
      <main className="main" {...(narrow && drawerOpen ? { inert: '' } : {})}>
        <header className="topbar">
          {narrow && (
            <button id="workspace-menu-toggle" className="icon-btn float-toggle" onClick={() => setDrawerOpen(true)} aria-label={tr('打开侧栏', 'Open sidebar')} aria-expanded={drawerOpen} aria-controls="workspace-sidebar">
              <Ic.Panel size={20} />
            </button>
          )}
          <div className="model-picker" onKeyDown={event => { if (event.key === 'Escape') { setMenuOpen(false); event.currentTarget.querySelector('button')?.focus(); } }} onClick={(e) => e.stopPropagation()}>
            <button aria-expanded={menuOpen} aria-controls="workspace-models" disabled={!registryIds || archivePending || therapyCreating} className="model-switch" onClick={() => setMenuOpen((o) => !o)}>
              {registryIds ? <ModelName id={model.id} fallback={modelDefaults[model.id]?.label || model.name} /> : tr("正在加载模型…", "Loading models…")} <Ic.Chev className="chev" size={18} />
            </button>
            {menuOpen && (
              <div className="menu" id="workspace-models" aria-label={tr('选择模型', 'Choose a model')}>
                {pickerModels.map((m) => (
                  <button key={m.id} className="menu-item" onClick={() => {
                    chooseModel(m); setMenuOpen(false);
                    // voice-only models can't do video — drop out of video mode.
                    if (m.video === false && mode === "video") setMode("voice");
                  }}>
                    <div style={{ flex: 1 }}>
                      <div className="mi-name">
                        <ModelName id={m.id} fallback={modelDefaults[m.id]?.label || m.name} />
                        {m.badge && !MODEL_DISPLAY[m.id] && <span className="chip chip--accent" style={{ background: "var(--accent-weak)", color: "var(--accent)" }}>{m.badge}</span>}
                      </div>
                      {!MODEL_DISPLAY[m.id] && <div className="mi-desc">{m.desc}</div>}
                    </div>
                    {m.id === model.id && <Ic.Check className="mi-check" size={18} />}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Always-visible chat/realtime mode switch. Both the indicator of the
              current upstream mode and the control to change it. Self-styled
              (.mode-seg in app.css) so it does NOT depend on the Tweaks panel's
              injected .twk-seg styles — those only exist while that hidden
              design-tool panel is open, which is why the old modal segment showed
              no visible selection ("点击无反应"). Switching reopens the Live
              session with the new ?realtime_mode= (Live.jsx dep on realtimeMode). */}
          {model.id === "qwen3-omni" && (
            <div className="mode-seg" role="radiogroup" aria-label="对话模式 Mode" title="对话模式：Chat = 本地上下文 + /v1/chat/completions；Realtime = 上游原生 /v1/realtime">
              {[{ v: "chat", l: "Chat" }, { v: "realtime", l: "Realtime" }].map((o) => (
                <button key={o.v} type="button" role="radio"
                        aria-checked={t.realtimeMode === o.v}
                        className={"mode-seg-btn" + (t.realtimeMode === o.v ? " on" : "")}
                        onClick={() => setTweak("realtimeMode", o.v)}>
                  {o.l}
                </button>
              ))}
            </div>
          )}
          {/* Thinking is displayed separately; voice reads only the spoken answer.
              The pinned model release selects native single-pass or legacy handling.
              Inert in Realtime mode: there the native upstream templates the
              prompt itself, so the gateway has no enable_thinking to set. */}
          {(modelDefaults[model.id]?.supportsThinking ?? THINKING_MODELS.has(model.id)) && (
            <div className="mode-seg" role="group" aria-label="Thinking">
              <button type="button" role="switch"
                      aria-checked={!!t.thinking}
                      disabled={t.realtimeMode === "realtime"}
                      className={"mode-seg-btn" + (t.thinking ? " on" : "")}
                      title={t.realtimeMode === "realtime"
                        ? "Realtime 模式由上游自己套模板，此开关不生效（切到 Chat 可用）"
                        : thinkingDescription}
                      onClick={() => setTweak("thinking", !t.thinking)}>
                {tr('思考', 'Thinking')} {t.thinking ? tr('开', 'On') : tr('关', 'Off')}
              </button>
            </div>
          )}
          <div className="spacer"></div>
        </header>
        {accountNotice}
        {therapyCreating && <p className="therapy-create-status" role="status">{tr('正在创建咨询…','Creating therapy…')}</p>}
        {therapyCreateError && !newTherapyOpen && <p className="therapy-create-status" role="alert">{therapyCreateError}</p>}
        {historyError && <p className="therapy-create-status" role="alert">{historyError}</p>}
        {stopError && <p className="therapy-create-status" role="alert">{stopError}</p>}

        {!registryIds ? <div className="model-loading" role={modelError ? 'alert' : 'status'}>
          {modelError ? <><p>{tr('模型加载失败，请重试。', 'Unable to load models. Please retry.')}</p><button onClick={() => setModelAttempt(n => n + 1)}>{tr('重新加载模型', 'Retry models')}</button></> : tr('正在加载模型…', 'Loading models…')}
        </div> : therapyCreating ? <div className="model-loading" /> : textCourse && mode === "text" && !!modelDefaults[model.id]?.courseCapable ? (
          <TextCourse key={`${modelSessionKey(model.id, modelDefaults[model.id])}:${therapyEpoch}`} thinking={t.thinking} locale={locale} onNewTherapy={() => setNewTherapyOpen(true)}
            initialCourseId={therapyStartCourse} onProgress={onTherapyProgress} onArchived={refreshTherapy} onExit={returnHome}
            model={model.id} coursePromptVersion={modelDefaults[model.id]?.coursePromptVersion || ""} />
        ) : (
          <ChatView disabled={historyLoading || deleting} hideSuggestions={!!services}
            locale={locale}
            conversationKey={`${model.id}:${activeId || 'new'}`}
            onStop={stopReply} stopping={stopping} canStop={!services || !!turnId}
            messages={messages}
            draft={draft}
            setDraft={setDraft}
            onSend={() => send()}
            onVoice={() => startMediaChat("voice")}
            onVideo={() => startMediaChat("video")}
            canVideo={model.video !== false}
            dictating={dictating}
            onDictate={toggleDictate}
          />
        )}
      </main>

      {newTherapyOpen && <NewTherapyDialog locale={locale}
        onSubmit={newTherapy} onClose={() => setNewTherapyOpen(false)} busy={therapyCreating}
        locked={!!pendingTherapy.current} error={therapyCreateError} />}
      {deleteTarget && <DeleteChatDialog locale={locale} title={deleteTarget.title} busy={deleting}
        error={deleteError} onClose={() => setDeleteTarget(null)} onConfirm={deleteChat} />}

      {registryIds && mode !== "text" && (
        <LiveMode key={`${modelSessionKey(model.id, modelDefaults[model.id])}:${voiceStartCourse}:${therapyEpoch}`}
          locale={locale} mode={mode} tweaks={t} model={model.id}
          initialCourseId={voiceStartCourse}
          modelName={<ModelName id={model.id} fallback={modelDefaults[model.id]?.label || model.name} />}
          /* Model capability alone must not turn ordinary chat into therapy. */
          courseCapable={!!voiceStartCourse && !!modelDefaults[model.id]?.courseCapable}
          coursePromptVersion={modelDefaults[model.id]?.coursePromptVersion || ""}
          onProgress={onTherapyProgress} onEnd={returnHome} />
      )}

      {promptOpen && <SystemPromptModal value={t.systemPrompt} defaultValue={modelDefaults[model.id]?.instructions || DEFAULT_SYSTEM_PROMPT} onSave={v => { setTweak("systemPrompt", v); setPromptOpen(false); }} onClose={() => setPromptOpen(false)} modelId={model.id} realtimeMode={t.realtimeMode} onRealtimeModeChange={v => setTweak("realtimeMode", v)} />}
      {!services && <TweaksPanel>
        <TweakSection label="Appearance" />
        <TweakColor label="Accent" value={t.accent}
          options={["#5b5bf0", "#8a5cf6", "#3b82f6", "#2dabc2", "#10b981"]}
          onChange={(v) => setTweak("accent", v)} />
        <TweakToggle label="Dark mode" value={t.dark} onChange={(v) => setTweak("dark", v)} />
        <TweakSection label="Voice / Video" />
        <TweakRadio label="Visualization" value={t.voiceViz}
          options={["orb", "wave"]} onChange={(v) => setTweak("voiceViz", v)} />
        <TweakRadio label="Transport" value={t.transport}
          options={["webrtc", "ws"]} onChange={(v) => setTweak("transport", v)} />
        <TweakSelect label="Voice" value={t.voice}
          options={["Ethan", "Chelsie", "Aiden"]} onChange={(v) => setTweak("voice", v)} />
        <TweakToggle label="数字人 Digital human" value={t.digitalHuman}
          onChange={(v) => setTweak("digitalHuman", v)} />
        {t.digitalHuman && (
          <TweakRadio label="形象 Portrait" value={t.portrait}
            options={[{ value: "", label: "默认" }, { value: "portrait_a", label: "形象A" }, { value: "portrait_b", label: "形象B" }]}
            onChange={(v) => setTweak("portrait", v)} />
        )}
      </TweaksPanel>}
    </div>
  );
}
